import React, { useState, useEffect } from 'react';
import { Mic, Video, StopCircle } from 'lucide-react';
import { useRecording } from '../context/RecordingContext';

interface RecordingControlsProps {
  showCamera: boolean;
  onToggleCamera: () => void;
  videoStream?: MediaStream | null;
  setShowCamera: (show: boolean) => void;
  wavesurfer?: WaveSurfer;
  songId: string;
  tournamentId?: string | null;
  tournamentRound?: number | null;
  onStartRecording?: (mode: 'audio' | 'video') => void; // New prop for custom start behavior
  onStopRecordingClick?: () => void; // New prop for handling stop button click
}

export const RecordingControls: React.FC<RecordingControlsProps> = ({
  showCamera,
  onToggleCamera,
  videoStream,
  setShowCamera,
  wavesurfer,
  songId,
  tournamentId,
  tournamentRound,
  onStartRecording,
  onStopRecordingClick
}) => {
  const { recording, startRecording, stopRecording } = useRecording();
  const [isStopping, setIsStopping] = useState(false);

  // Reset isStopping when recording state changes
  useEffect(() => {
    if (!recording.isRecording) {
      setIsStopping(false);
    }
  }, [recording.isRecording]);

  // Handle direct recording start
  const handleStartRecording = (mode: 'audio' | 'video') => {
    if (onStartRecording) {
      onStartRecording(mode);
    } else {
      startRecording(mode, videoStream);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col gap-2 min-h-[96px]">
      {!recording.isRecording ? (
        <>
          <button
            onClick={onToggleCamera}
            className={`p-2 rounded-full transition-colors ${
              recording.recordingUrl ? 'bg-tsl-purple-700/50 text-white/50 cursor-not-allowed' :
              showCamera ? 'bg-tsl-accent text-white hover:bg-tsl-accent-dark' :
              'bg-tsl-purple-700 hover:bg-tsl-purple-600'
            }`}
            title={showCamera ? 'Turn Off Camera' : 'Turn On Camera'}
            disabled={!!recording.recordingUrl}
          >
            <Video size={24} />
          </button>
          <button
            onClick={() => handleStartRecording(showCamera ? 'video' : 'audio')}
            className={`p-2 rounded-full transition-colors ${
              recording.recordingUrl ? 'bg-tsl-purple-700/50 text-white/50 cursor-not-allowed' :
              showCamera ? 'bg-tsl-accent text-white hover:bg-tsl-accent-dark' :
              'bg-tsl-purple-700 hover:bg-tsl-purple-600'
            }`}
            title={showCamera ? 'Record Video' : 'Record Audio'}
            disabled={!!recording.recordingUrl}
          >
            <Mic size={24} />
          </button>
        </>
      ) : (
        <button
          onClick={() => {
            if (onStopRecordingClick) {
              onStopRecordingClick();
            } else {
              // Fallback to original behavior if no custom handler provided
              setIsStopping(true);
              stopRecording(songId, () => {
                setShowCamera(false);
                setIsStopping(false);

                // Also stop the wavesurfer playback
                if (wavesurfer) {
                  wavesurfer.pause();
                }
              }, wavesurfer, tournamentId, tournamentRound);
            }
          }}
          className={`p-2 rounded-full ${
            isStopping ? 'bg-red-500/50 cursor-not-allowed' : 'bg-red-500 hover:bg-red-600'
          } text-white transition-colors relative`}
          title="Stop Recording"
          disabled={isStopping}
        >
          <StopCircle size={24} />
          <span className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs bg-tsl-purple-800 px-2 py-1 rounded">
            {formatTime(recording.recordingTime)}
          </span>
        </button>
      )}
    </div>
  );
};